import { i18n } from '@lingui/core'
import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { Chip } from '@/@bothrs-ui/web/components/chip/Chip'
import { FlexColumn, FlexRow, Padding } from '@/@bothrs-ui/web/components/layout'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { globalDefinitions } from '@/defined-translations'
import { isIntramuros } from '@/utils/intramuros'

import type { FC, ReactNode } from 'react'

type DocumentLayoutStatic = {
  Header: typeof DocumentLayoutHeader
  Content: typeof DocumentLayoutContent
  Footer: typeof DocumentLayoutFooter
}

export type Props = {
  /**
   * Content to be displayed
   */
  children?: ReactNode

  className?: string
}

/**
 * Maximum width for the content container
 */

const DocumentLayout: FC<Props> & DocumentLayoutStatic = ({ children, className }: Props) => {
  return (
    <StContainer className={className}>
      {/* Compound components: Include Content and Footer components */}
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          const { type } = child
          if (type === DocumentLayoutHeader || type === DocumentLayoutContent || type === DocumentLayoutFooter) {
            return child
          }
        }

        return null
      })}
    </StContainer>
  )
}

export const DocumentLayoutHeader = ({ document, signState }: { document: any; signState?: ReactNode }) => {
  const isMobileView = useMediaQuery(breakpoints.toS)

  const navigate = useNavigate()
  const theme = useTheme()
  const location = useLocation()

  const renderCareplanChip = () => {
    if (document.type === 'Hylyght') {
      return <StChip type='Primary'>Hylyght</StChip>
    }

    if (document?.careplanDisplay) {
      return <StChip type='Primary'>{document?.careplanDisplay}</StChip>
    }

    return null
  }

  return (
    <StHeader>
      {/* App Header on mobile */}
      {isMobileView ? (
        <StMobileHeader>
          <AppHeader
            primaryAction={{
              label: i18n._(globalDefinitions.goBack),
              icon: ['far', 'chevron-left'],
              onClick: () => {
                if (location.state?.from === 'timeline') {
                  navigate(-1)
                } else {
                  if (isIntramuros) {
                    navigate('../..', {
                      relative: 'path',
                      state: { documentId: document.id },
                    })
                  } else {
                    navigate('..', {
                      relative: 'path',
                      state: { documentId: document.id },
                    })
                  }
                }
              },
            }}
          />
        </StMobileHeader>
      ) : null}

      <StMobileHeader>
        <H3 noMargin>{document?.display}</H3>
        {signState}
      </StMobileHeader>

      {renderCareplanChip() ? (
        <StHeaderContent>
          <FlexRow justifyContent='space-between' alignItems='center'>
            {/* <TaskStatusLabel task={task} display={statusLabel} variant="detail" /> */}
            {renderCareplanChip()}
          </FlexRow>
        </StHeaderContent>
      ) : null}

      {/* Line */}
      <Padding py={theme.UI.Spacing.Space6} px={theme.UI.Spacing.Space6}>
        <Line />
      </Padding>
    </StHeader>
  )
}

export const DocumentLayoutContent = ({ children }: Props) => {
  return <StContentContainer>{children}</StContentContainer>
}

export const DocumentLayoutFooter = ({ children }: Props) => {
  return (
    <StFooter>
      <StFooterContent>{children}</StFooterContent>
    </StFooter>
  )
}

const StContainer = styled(FlexColumn)`
  flex: 1;
  height: 100%;
  position: relative;
`

const StHeader = styled(FlexColumn)`
  flex-shrink: 1;
  width: 100%;
`

const StHeaderContent = styled(FlexColumn)`
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
  padding: ${({ theme }) => `${theme.UI.SpacingPx.Space4} ${theme.UI.SpacingPx.Space6}`};
  width: 100%;

  margin: 0 auto;
`

const StContentContainer = styled(FlexColumn)`
  flex: 1;
  width: 100%;
  overflow-y: auto;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space6};
  padding-top: 0;
  margin: 0 auto;
`

const StChip = styled(Chip)`
  flex-shrink: 0;
  flex-wrap: nowrap;
  white-space: nowrap;

  font-size: ${({ theme }) => theme.Typography.BodySmallRegular['font-size']} !important;
  font-weight: ${({ theme }) => theme.Typography.BodySmallRegular['font-weight']} !important;
  line-height: ${({ theme }) => theme.Typography.BodySmallRegular['line-height']} !important;
  letter-spacing: ${({ theme }) => theme.Typography.BodySmallRegular['letter-spacing']} !important;
`

const StMobileHeader = styled.div`
  padding: ${({ theme }) => `${theme.UI.SpacingPx.Space4} ${theme.UI.SpacingPx.Space6}`};
  padding-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space3};
`

const StFooter = styled.footer`
  flex-shrink: 1;
  box-shadow: 0px -1px 20px rgba(4, 24, 49, 0.07);

  background-color: ${({ theme }) => theme.Colors.Background.White};
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4}
    ${({ theme }) => theme.UI.SpacingPx.Space2};
  padding-bottom: calc(
    max(var(--safe-area-inset-bottom, 0px), 20px) +
      ${({ theme }) => theme.UI.Spacing.Space1}px
  );
  @media (${breakpoints.fromM}) {
    padding-bottom: calc(
      max(var(--safe-area-inset-bottom, 0px), 20px) +
        ${({ theme }) => theme.UI.Spacing.Space6}px
    );
  }

  position: sticky;
  align-items: center;

  bottom: 0;
  z-index: 1;
`

const StFooterContent = styled(FlexColumn)`
  @media (${breakpoints.fromM}) {
    max-width: 600px;
  }
  margin: 0 auto;
`

export const StDocumentParagraph = styled.p`
  ${({ theme }) => theme.Typography.BodyMediumRegular};
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  margin: 0;
`

DocumentLayout.Header = DocumentLayoutHeader
DocumentLayout.Content = DocumentLayoutContent
DocumentLayout.Footer = DocumentLayoutFooter

export { DocumentLayout }
